import { useState, useEffect } from "react";
import { Phone, Menu, X } from "lucide-react";
import { AGENCY } from "@/data/content";
import { useQuote } from "@/context/QuoteContext";

const LINKS = [
  { label: "Coverage", href: "#products" },
  { label: "About", href: "#about" },
  { label: "Reviews", href: "#reviews" },
  { label: "Service", href: "#service" },
  { label: "Claims", href: "#claims" },
  { label: "Contact", href: "#contact" },
];

export default function Header() {
  const { openQuote } = useQuote();
  const [scrolled, setScrolled] = useState(false);
  const [menu, setMenu] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-brand-cream/80 backdrop-blur-xl border-b border-brand-line" : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8 h-[72px] flex items-center justify-between">
        <a href="#top" data-testid="logo-link" className="flex items-center gap-2.5 group">
          <span className="grid h-9 w-9 place-items-center rounded-full bg-brand-green text-brand-cream font-serif text-lg">D</span>
          <span className="leading-tight max-w-[168px] sm:max-w-[300px]">
            <span className={`block font-serif text-[13px] leading-[1.08] sm:text-base transition-colors ${scrolled ? "text-brand-ink" : "text-brand-cream"}`}>{AGENCY.name}</span>
            <span className={`block font-mono text-[7px] sm:text-[8px] uppercase tracking-[0.12em] mt-0.5 transition-colors ${scrolled ? "text-brand-moss" : "text-brand-cream/70"}`}>{AGENCY.legalName}</span>
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-8">
          {LINKS.map((l) => (
            <a key={l.href} href={l.href} data-testid={`nav-${l.label.toLowerCase()}`}
              className={`text-sm transition-colors ${scrolled ? "text-brand-ink/80 hover:text-brand-green" : "text-brand-cream/85 hover:text-brand-cream"}`}>
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          <a href={AGENCY.phoneHref} data-testid="header-call-btn"
            className={`hidden sm:flex items-center gap-2 text-sm font-medium transition-colors ${scrolled ? "text-brand-green hover:text-brand-clay" : "text-brand-cream hover:text-brand-sand"}`}>
            <Phone className="h-4 w-4" /> {AGENCY.phone}
          </a>
          <button onClick={() => openQuote()} data-testid="header-quote-btn"
            className={`rounded-full text-sm font-medium px-5 py-2.5 active:scale-95 transition-all ${scrolled ? "bg-brand-green text-brand-cream hover:bg-brand-ink" : "bg-brand-cream text-brand-green hover:bg-white"}`}>
            Get a Quote
          </button>
          <button className={`lg:hidden p-2 transition-colors ${scrolled ? "text-brand-ink" : "text-brand-cream"}`} onClick={() => setMenu(!menu)} data-testid="mobile-menu-btn">
            {menu ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {menu && (
        <div className="lg:hidden bg-brand-cream border-b border-brand-line px-5 py-4 space-y-1" data-testid="mobile-menu">
          {LINKS.map((l) => (
            <a key={l.href} href={l.href} onClick={() => setMenu(false)}
              className="block py-2.5 text-brand-ink border-b border-brand-line/50 last:border-0">
              {l.label}
            </a>
          ))}
          <a href={AGENCY.phoneHref} className="flex items-center gap-2 py-2.5 text-brand-green font-medium">
            <Phone className="h-4 w-4" /> {AGENCY.phone}
          </a>
        </div>
      )}
    </header>
  );
}
